import { Site } from "@inrange/building-manager-api-client/models-site";
import { BarDatum, ResponsiveBar } from "@nivo/bar";
import React from "react";
import InRangeLogo from "../images/inrange-logo.svg";
import { ChartData } from "./charts/chart-types";

const formatNumber = (
  value: number,
  minimumNumFractionDigits: number,
  maximumFractionDigits: number,
  useGrouping: boolean = true
): string => {
  if (value === 0) value += 0;
  const numberFormat = new Intl.NumberFormat("en-GB", {
    style: "decimal",
    minimumFractionDigits: minimumNumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
    //@ts-expect-error - TS doesn't know about the roundingMode option
    roundingMode: "halfCeil",
    useGrouping: useGrouping,
  });
  return numberFormat.format(value);
};

const formatNumber2dp = (value: number): string => formatNumber(value, 2, 2);

const roundToDp = (value: number, dp: number): number => {
  return Math.round(value * Math.pow(10, dp)) / Math.pow(10, dp);
};

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const theme = {
  textColor: "rgb(128, 128, 128, 0.7)",
  axis: {
    fontColor: "rgb(128, 128, 128, 0.7)",
    ticks: {
      line: {
        stroke: "rgb(128, 128, 128, 0.7)",
      },
      text: {
        fill: "rgb(128, 128, 128, 0.7)",
      },
    },
  },
  grid: {
    line: {
      stroke: "rgb(223, 223, 223, 0.3)",
    },
  },
};

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

type Step = {
  label: string;
  barLabel: string;
  value: number;
  isTotal?: boolean;
};

type WaterfallDatum = {
  label: string;
  hidden: number;
  visible: number;
  barLabel: string;
  isTotal: string;
  displayValue: number;
};

const WaterfallChart = ({ steps }: { steps: Step[] }) => {
  let cumulative = 0;

  const rawData: WaterfallDatum[] = steps.map((step, index) => {
    const delta = index > 0 ? step.value - steps[index - 1].value : step.value;

    let hidden = 0;
    let visible = 0;

    if (step.isTotal) {
      hidden = 0;
      visible = step.value;
      cumulative = step.value;
    } else {
      if (delta >= 0) {
        hidden = cumulative;
        visible = delta;
        cumulative += delta;
      } else {
        // For decreases, we offset the bar left by change, and move cumulative down
        hidden = cumulative + delta; // Start position of the decrease
        visible = -delta; // Always positive width
        cumulative += delta;
      }
    }

    return {
      label: step.label,
      hidden,
      visible,
      barLabel: step.barLabel,
      isTotal: step.isTotal ? "true" : "false",
      displayValue: step.isTotal ? step.value : delta,
    };
  });

  // Reverse for top-down layout
  const data = [...rawData].reverse();

  return (
    <div style={{ height: "calc(100% - 40px)" }}>
      <ResponsiveBar
        data={data}
        indexBy="label"
        keys={["hidden", "visible"]}
        layout="horizontal"
        groupMode="stacked"
        enableGridX={false}
        margin={{ top: 20, right: 80, bottom: 45, left: 180 }}
        padding={0.4}
        enableLabel={false}
        colors={({ id, data }) => {
          if (id === "hidden") return "transparent";
          if (data.isTotal === "true") return "#00022F";
          return data.displayValue >= 0 ? "#4caf50" : "#f44336"; // green/red
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 12,
        }}
        axisBottom={null}
        enableGridY={false}
        tooltip={({ data }) => (
          <div
            style={{ padding: 6, background: "#fff", border: "1px solid #ccc" }}
          >
            <strong>{data.label}</strong>:{" "}
            {formatNumber(data.displayValue, 0, 0, true)} kWh
          </div>
        )}
        layers={[
          "grid",
          "axes",
          "bars",
          "markers",
          "legends",
          "annotations",
          ({ bars }) =>
            bars
              .filter((bar) => bar.data.id !== "hidden")
              .map((bar) => {
                const isTotal = bar.data.data.isTotal === "true";
                const value = bar.data.data.barLabel;
                const labelX = isTotal ? bar.x + 8 : bar.x + bar.width + 5;
                const labelY = bar.y + bar.height / 2 + 5;

                return (
                  <text
                    key={bar.key}
                    x={labelX}
                    y={labelY}
                    textAnchor={isTotal ? "start" : "start"}
                    alignmentBaseline="middle"
                    style={{
                      fontSize: 12,
                      fill: isTotal ? "#FFF" : "#000",
                      fontWeight: isTotal ? 600 : 400,
                    }}
                  >
                    {value}
                  </text>
                );
              }),
        ]}
        theme={{
          axis: {
            domain: {
              line: {
                stroke: "#CCC", // ensures the axis line is visible
                strokeWidth: 1,
              },
            },
          },
        }}
      />
    </div>
  );
};

interface SitePvReportProps {
  site: Site;
  pvPanel: {
    manufacturer: string;
    model: string;
    pnom: number;
    height: number;
    width: number;
  };
  pvInverters: Array<{
    name: string;
    manufacturer: string;
    model: string;
    ratedAcOutputPower: number;
    count: number;
  }>;
  inverterTotalPowerKwAc: number;
  lossFactorLabels: Record<string, string>;
}

const SitePvReport: React.FC<SitePvReportProps> = ({
  site,
  pvPanel,
  pvInverters,
  inverterTotalPowerKwAc,
  lossFactorLabels,
}) => {
  document.title = `${site.name
    .replace(/[^\w\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()} Simulation Report ${new Date().toISOString().split("T")[0]}`;

  const generationPr =
    (site.aggHalfHourlyGeneration.genStack.stack[
      site.aggHalfHourlyGeneration.genStack.stack.length - 1
    ].after /
      (site.aggHalfHourlyGeneration.genStack.stack[0].after /
        site.aggHalfHourlyGeneration.yoyVarInvLossFactor)) *
    100;

  const normalisedProductionData: ChartData[] = Object.entries(
    site.aggHalfHourlyGeneration.genStackMonthly
  )
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([month, genStackMonth]) => {
      const generationFromGti = genStackMonth.stack[0].after;
      const generationAfterCollectionLosses = genStackMonth.stack.find(
        (step) => step.factor === "MOM"
      )!.after;
      const generationFinal =
        genStackMonth.stack[genStackMonth.stack.length - 1].after;
      return {
        index: months[parseInt(month) - 1],
        "Produced useful energy (Yf)": roundToDp(
          generationFinal /
            site.totalInstalledCapacity /
            getDaysInMonth(
              site.energyFlowMonthly[month].year,
              site.energyFlowMonthly[month].month
            ),
          2
        ),
        "System loss (Ls)": roundToDp(
          (generationAfterCollectionLosses -
            site.energyFlowMonthly[month].generation) /
            site.totalInstalledCapacity /
            getDaysInMonth(
              site.energyFlowMonthly[month].year,
              site.energyFlowMonthly[month].month
            ),
          2
        ),
        "Collection loss (Lc)": roundToDp(
          (generationFromGti - generationAfterCollectionLosses) /
            site.totalInstalledCapacity /
            getDaysInMonth(
              site.energyFlowMonthly[month].year,
              site.energyFlowMonthly[month].month
            ),
          2
        ),
      };
    });
  const normalisedProductionKeys = normalisedProductionData.reduce(
    (acc, curr) => {
      Object.keys(curr).forEach((key) => {
        if (key !== "index" && !acc.includes(key)) {
          acc.push(key);
        }
      });
      return acc;
    },
    [] as string[]
  );
  const normalisedProductionColors = ["#00022F", "#9f9f9f", "#2779A7"];

  const prData: ChartData[] = Object.entries(
    site.aggHalfHourlyGeneration.genStackMonthly
  )
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([month, genStackMonth]) => {
      const generationFromGti = genStackMonth.stack[0].after;
      const generationFinal =
        genStackMonth.stack[genStackMonth.stack.length - 1].after;
      return {
        index: months[parseInt(month) - 1],
        "PR: Performance Ratio (Yf / Yr)": roundToDp(
          (generationFinal / generationFromGti) * 100,
          2
        ),
      };
    });
  const prDataKeys = prData.reduce((acc, curr) => {
    Object.keys(curr).forEach((key) => {
      if (key !== "index" && !acc.includes(key)) {
        acc.push(key);
      }
    });
    return acc;
  }, [] as string[]);
  const prDataColors = ["#00022F"];

  const addLossFactors = (startLossFactor: string, endLossFactor: string) => {
    let started = false;
    for (
      let i = 0;
      i < site.aggHalfHourlyGeneration.genStack.stack.length;
      i++
    ) {
      const factor = site.aggHalfHourlyGeneration.genStack.stack[i];
      if (factor.factor === startLossFactor) {
        started = true;
      }
      if (!started) {
        continue;
      }
      if (factor.pct !== 0) {
        lossFactorSteps.push({
          label: lossFactorLabels[factor.factor] ?? factor.factor,
          barLabel: `${roundToDp(-1 * factor.pct * 100, 2)}%`,
          value: factor.after,
        });
      }
      if (factor.factor === endLossFactor) {
        break;
      }
    }
  };
  const lossFactorSteps: Step[] = [
    {
      label: "Global horizontal irradiance",
      barLabel: `${formatNumber(site.generation[0].halfHourlyGeneration!.ghi, 0, 0, false)} kWh/m2`,
      value: site.aggHalfHourlyGeneration.genStack.inputGen,
      isTotal: true,
    },
  ];
  addLossFactors("GICP", "SOIL");
  lossFactorSteps.push({
    label: "Energy after PV conversion",
    barLabel: `${formatNumber(lossFactorSteps[lossFactorSteps.length - 1].value, 0, 0, true)} kWh`,
    value: lossFactorSteps[lossFactorSteps.length - 1].value,
    isTotal: true,
  });
  addLossFactors("NSE", "MOM");
  lossFactorSteps.push({
    label: "Available energy at inverter input",
    barLabel: `${formatNumber(lossFactorSteps[lossFactorSteps.length - 1].value, 0, 0, true)} kWh`,
    value: lossFactorSteps[lossFactorSteps.length - 1].value,
    isTotal: true,
  });
  addLossFactors("IE", "IVT");
  lossFactorSteps.push({
    label: "Available energy at inverter output",
    barLabel: `${formatNumber(lossFactorSteps[lossFactorSteps.length - 1].value, 0, 0, true)} kWh`,
    value: lossFactorSteps[lossFactorSteps.length - 1].value,
    isTotal: true,
  });
  addLossFactors("NC", "IBEX");
  lossFactorSteps.push({
    label: "Usable energy",
    barLabel: `${formatNumber(lossFactorSteps[lossFactorSteps.length - 1].value, 0, 0, true)} kWh`,
    value: lossFactorSteps[lossFactorSteps.length - 1].value,
    isTotal: true,
  });

  return (
    <>
      <style>
        {`
    @page {
      margin: 0;
    }

    @media print {
      body {
        margin: 0;
        padding: 0;
      }
    }

    body {
      font-size: 12px;
    }

    th {
      font-weight: normal;
    }

    .pv-report table td,
    .pv-report table th {
      padding: 4px 8px;
    }
  `}
      </style>

      <div
        className="pv-report"
        style={{
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          backgroundColor: "#F5F5F5",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            margin: "0 calc(1.5rem * .5)",
            fontSize: "28px",
            fontWeight: "bold",
            display: "flex",
            flexDirection: "row",
            justifyContent: "start",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <img
            src={InRangeLogo}
            alt="InRange Logo"
            height={32}
            style={{ width: "auto" }}
          />
          <div>Simulation report</div>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            margin: 0,
            fontSize: "20px",
          }}
        >
          <div style={{ flex: 1 }}>Project: {site.name}</div>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            margin: 0,
            padding: "8px 12px",
            backgroundColor: "#FFF",
            borderRadius: "4px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Geographical site
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Weather data
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Address: {site.address}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                SolarGIS GHI (Dataset: 2025-04)
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Lat, Long: {site.latitude}, {site.longitude}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                PVGIS TMY (2005-2023)
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            margin: 0,
            padding: "8px 12px",
            backgroundColor: "#FFF",
            borderRadius: "4px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>PV array</div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>Inverters</div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                Nb. of modules:{" "}
              </div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                {formatNumber(site.pvPanelCount, 0, 0)} panels
              </div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                Nb. of units:{" "}
              </div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                {pvInverters.reduce((acc, curr) => acc + curr.count, 0)} units
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                Pnom total:{" "}
              </div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                {formatNumber(
                  (site.pvPanelCount * pvPanel.pnom) / 1000,
                  0,
                  0
                )}{" "}
                kWp
              </div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                Pnom total:{" "}
              </div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                {formatNumber(inverterTotalPowerKwAc, 0, 3)} kWac
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}></div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                Pnom ratio:{" "}
              </div>
              <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                {formatNumber(
                  site.totalInstalledCapacity / inverterTotalPowerKwAc,
                  0,
                  2,
                  false
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>Grid connected, No shadings</div>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            margin: 0,
            padding: "8px 12px",
            backgroundColor: "#FFF",
            borderRadius: "4px",
            pageBreakInside: "avoid",
          }}
        >
          <div>
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              Main results
            </div>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  Produced energy:
                </div>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  {formatNumber2dp(site.energyFlowAnnual.generation / 1000.0)}{" "}
                  MWh/year
                </div>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  Perf Ratio PR:
                </div>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  {formatNumber2dp(generationPr)}%
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  Specific production:
                </div>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  {formatNumber(
                    site.energyFlowAnnual.generation /
                      site.totalInstalledCapacity,
                    0,
                    0,
                    false
                  )}{" "}
                  kWh/kWp/year
                </div>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  Forecast probability:
                </div>
                <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
                  {site.pvForecastProbability}
                </div>
              </div>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              marginTop: "12px",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "360px",
              }}
            >
              <div style={{ fontWeight: "bold" }}>
                Normalised productions (per installed kWp)
              </div>
              <div
                style={{ display: "flex", flexDirection: "row", gap: "8px" }}
              >
                {normalisedProductionKeys.map((key, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "8px",
                      fontSize: "8px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        height: "8px",
                        width: "16px",
                        backgroundColor: normalisedProductionColors[index],
                        borderRadius: 2,
                      }}
                    ></div>
                    <div>{key}</div>
                  </div>
                ))}
              </div>
              <div style={{ height: "200px", width: "360px" }}>
                <ResponsiveBar
                  data={normalisedProductionData as BarDatum[]}
                  indexBy={"index"}
                  groupMode={"stacked"}
                  keys={normalisedProductionKeys}
                  margin={{
                    left: 70,
                    bottom: 35,
                    top: 10,
                  }}
                  padding={0.3}
                  colors={normalisedProductionColors}
                  colorBy={normalisedProductionData["index"]}
                  borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 1,
                    tickRotation: 0,
                    legendPosition: "middle",
                    legendOffset: -50,
                    legend: "kWh / kWp / day",
                  }}
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: 0,
                    legendPosition: "middle",
                    legendOffset: 32,
                  }}
                  theme={theme}
                  animate={false}
                  enableLabel={false}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "360px",
              }}
            >
              <div style={{ fontWeight: "bold" }}>Performance Ratio PR</div>
              <div
                style={{ display: "flex", flexDirection: "row", gap: "8px" }}
              >
                {prDataKeys.map((key, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "8px",
                      fontSize: "8px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        height: "8px",
                        width: "16px",
                        backgroundColor: prDataColors[index],
                        borderRadius: 2,
                      }}
                    ></div>
                    <div>{key}</div>
                  </div>
                ))}
              </div>
              <div style={{ height: "200px", width: "360px" }}>
                <ResponsiveBar
                  data={prData as BarDatum[]}
                  indexBy={"index"}
                  groupMode={"stacked"}
                  keys={prDataKeys}
                  margin={{
                    left: 70,
                    bottom: 35,
                    top: 10,
                  }}
                  padding={0.3}
                  colors={prDataColors}
                  colorBy={prData["index"]}
                  borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 1,
                    tickRotation: 0,
                    legendPosition: "middle",
                    legendOffset: -50,
                    legend: "Performance Ratio PR",
                  }}
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: 0,
                    legendPosition: "middle",
                    legendOffset: 32,
                  }}
                  theme={theme}
                  animate={false}
                  enableLabel={false}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            margin: 0,
            padding: "8px 12px",
            backgroundColor: "#FFF",
            borderRadius: "4px",
            pageBreakInside: "avoid",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
            Balances and main results
          </div>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                margin: "0 8px",
                textAlign: "right",
              }}
            >
              <thead>
                <tr>
                  <th></th>
                  <th>
                    GlobHor (kWh/m<sup>2</sup>)
                  </th>
                  <th>EArray (kWh)</th>
                  <th>E_User (kWh)</th>
                  <th>E_Solar (kWh)</th>
                  <th>E_Grid (kWh)</th>
                  <th>EFrGrid (kWh)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(site.energyFlowMonthly)
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .map(([month, energyFlowMonthly]) => (
                    <tr
                      key={month}
                      style={{
                        borderBottom:
                          month === "12" ? "1px solid #666" : undefined,
                      }}
                    >
                      <td style={{ fontWeight: "bold", textAlign: "left" }}>
                        {months[parseInt(month) - 1]}
                      </td>
                      <td>
                        {formatNumber(
                          site.aggHalfHourlyGeneration.genStackMonthly[
                            parseInt(month)
                          ].inputGen /
                            site.aggHalfHourlyGeneration.yoyVarInvLossFactor /
                            site.totalInstalledCapacity,
                          1,
                          1,
                          false
                        )}
                      </td>
                      <td>
                        {formatNumber(energyFlowMonthly.generation, 0, 0, true)}
                      </td>
                      <td>
                        {formatNumber(energyFlowMonthly.demand, 0, 0, true)}
                      </td>
                      <td>
                        {formatNumber(
                          energyFlowMonthly.behindMeterPV,
                          0,
                          0,
                          true
                        )}
                      </td>
                      <td>
                        {formatNumber(energyFlowMonthly.exportedPV, 0, 0, true)}
                      </td>
                      <td>
                        {formatNumber(energyFlowMonthly.gridImport, 0, 0, true)}
                      </td>
                    </tr>
                  ))}
                <tr style={{ borderBottom: "1px solid #666" }}>
                  <td style={{ fontWeight: "bold", textAlign: "left" }}>
                    Year
                  </td>
                  <td>
                    {formatNumber(
                      site.aggHalfHourlyGeneration.genStack.inputGen /
                        site.aggHalfHourlyGeneration.yoyVarInvLossFactor /
                        site.totalInstalledCapacity,
                      1,
                      1,
                      false
                    )}
                  </td>
                  <td>
                    {formatNumber(site.energyFlowAnnual.generation, 0, 0, true)}
                  </td>
                  <td>
                    {formatNumber(site.energyFlowAnnual.demand, 0, 0, true)}
                  </td>
                  <td>
                    {formatNumber(
                      site.energyFlowAnnual.behindMeterPV,
                      0,
                      0,
                      true
                    )}
                  </td>
                  <td>
                    {formatNumber(site.energyFlowAnnual.exportedPV, 0, 0, true)}
                  </td>
                  <td>
                    {formatNumber(site.energyFlowAnnual.gridImport, 0, 0, true)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                flex: "0 0 50%",
                maxWidth: "50%",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              Legend
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              fontSize: "10px",
            }}
          >
            <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  GlobHor: Global horizontal irradiation
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  EArray: Effective energy at array output
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  E_User: Energy supplied to the user
                </div>
              </div>
            </div>
            <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>E_Solar: Energy from the sun</div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  E_Grid: Energy injected into grid
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>EFrGrid: Energy from the grid</div>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            margin: 0,
            padding: "8px 12px",
            backgroundColor: "#FFF",
            borderRadius: "4px",
            pageBreakInside: "avoid",
            height: "800px",
            width: "750px",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
            Loss diagram
          </div>
          <WaterfallChart steps={lossFactorSteps} />
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            margin: 0,
            padding: "8px 12px",
            backgroundColor: "#FFF",
            borderRadius: "4px",
            pageBreakInside: "avoid",
          }}
        >
          <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              <div style={{ flex: 1 }}>PV module</div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Manufacturer:{" "}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {pvPanel.manufacturer}
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>Model: </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {pvPanel.model}
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Unit nom. power:{" "}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {pvPanel.pnom} Wp
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Number of PV modules:{" "}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {formatNumber(site.pvPanelCount, 0, 0)} units
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                marginTop: "16px",
              }}
            >
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Nominal (STC):{" "}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {formatNumber(
                  (site.pvPanelCount * pvPanel.pnom) / 1000,
                  0,
                  0,
                  true
                )}{" "}
                kWp
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Module area:{" "}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {formatNumber(
                  pvPanel.height * pvPanel.width * site.pvPanelCount,
                  0,
                  0
                )}{" "}
                m<sup>2</sup>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>LID: </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {formatNumber2dp(
                  (site.generationLossFactors["LID"] ?? 0) * 100
                )}
                %
              </div>
            </div>
          </div>
          <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              <div style={{ flex: 1 }}>Inverter</div>
            </div>
            {pvInverters.map((inverter, index) => (
              <React.Fragment key={index}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    ...(index > 0 ? { marginTop: "16px" } : {}),
                  }}
                >
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    Manufacturer:{" "}
                  </div>
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    {inverter.manufacturer}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    Model:{" "}
                  </div>
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    {inverter.model}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    Unit nom. power:{" "}
                  </div>
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    {formatNumber2dp(inverter.ratedAcOutputPower / 1000)} kWac
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    Number of inverters:{" "}
                  </div>
                  <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                    {inverter.count} units
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                marginTop: "16px",
              }}
            >
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Total power:{" "}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {formatNumber(inverterTotalPowerKwAc, 0, 3, false)} kWac
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                Pnom ratio:{" "}
              </div>
              <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
                {formatNumber(
                  site.totalInstalledCapacity / inverterTotalPowerKwAc,
                  0,
                  2,
                  false
                )}
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              width: "100%",
              marginTop: "8px",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            <div style={{ flex: "0 0 50%", maxWidth: "50%" }}>
              SolarEdge Power Optimizer
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
            <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>Model</div>
            <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>S1000</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
            <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
              Unit Nom. Power
            </div>
            <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>1000W</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
            <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
              Number of optimizers
            </div>
            <div style={{ flex: "0 0 25%", maxWidth: "25%" }}>
              {Math.ceil(site.pvPanelCount / 2)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SitePvReport;
